import React from 'react'
import { Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'

type Props = {
    list: string[]
}

const Approvers = ({ list }: Props) => {
    const { t } = useTranslation()

    // Function to get initials from a name
    const getInitials = (name: string): string => {
        if (!name || name.trim() === '') return ''

        const words = name.trim().split(' ').filter(word => word.length > 0)

        if (words.length === 1) {
            // If only one word, take the first letter
            return words[0].charAt(0).toUpperCase()
        } else {
            // If multiple words, take the first two letters from the first two words
            return words.slice(0, 2).map(word => word.charAt(0).toUpperCase()).join('')
        }
    }

    // Filter empty names and sort alphabetically
    const validApprovers = list
        .filter(name => name && name.trim() !== '')
        .sort((a, b) => a.trim().toLowerCase().localeCompare(b.trim().toLowerCase()))

    if (validApprovers.length === 0) {
        return <span className="text-sm text-gray-400">-</span>
    }

    const maxVisible = 4
    const visibleApprovers = validApprovers.slice(0, maxVisible)
    const remainingCount = validApprovers.length - maxVisible
    const hasMore = remainingCount > 0

    return (
        <div className="flex flex-wrap gap-1 items-center justify-center">
            {/* Show initials for the first 8 approvers */}
            {visibleApprovers.map((approver, index) => (
                <Tooltip
                    key={index}
                    placement="top"
                    title={approver}
                    color='#4d4c4c'
                >
                    <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-medium text-white bg-gray-500 rounded-full cursor-default">
                        {getInitials(approver)}
                    </span>
                </Tooltip>
            ))}

            {/* Show "+X" if there are more than 8 approvers */}
            {hasMore && (
                <Tooltip
                    placement="top"
                    title={
                        <div>
                            <div className="font-semibold mb-1">{t('discoveryReports.accordion.allApprovers')}:</div>
                            {validApprovers.map((approver, index) => (
                                <div key={index} className="text-sm">
                                    {approver}
                                </div>
                            ))}
                        </div>
                    }
                    color='#4d4c4c'
                >
                    <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-medium text-white bg-red-500 rounded-full cursor-default">
                        +{remainingCount}
                    </span>
                </Tooltip>
            )}
        </div>
    )
}

export default Approvers